/* src/pages/AcceptInvite.jsx
 * Accept team invitation page — resolves the invite token first so the
 * invited user sees who invited them and to which workspace, before they
 * have to create an account or log in.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { inviteApi } from '../utils/api';
import '../styles/auth.css';

const PENDING_TOKEN_KEY = 'pendingInviteToken';
const PENDING_CONTEXT_KEY = 'pendingInviteContext';

export default function AcceptInvite() {
  const { currentUser, logout } = useAuth();
  const { switchWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [phase, setPhase] = useState('loading'); // loading | invalid | already-accepted | context | mismatch | accepting | success | error
  const [invite, setInvite] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'gocta.ai - Accept invitation';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  const handleAccept = useCallback(async () => {
    if (!token || !currentUser) return;
    setPhase('accepting');
    try {
      const result = await inviteApi.accept(token);
      if (result.workspaceId) {
        localStorage.setItem(`workspace_${currentUser.uid}`, result.workspaceId);
        switchWorkspace(result.workspaceId);
        const { reload } = await import('firebase/auth');
        const { auth } = await import('../firebase');
        if (auth.currentUser) {
          await reload(auth.currentUser);
        }
      }
      setPhase('success');
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (err) {
      if (err.message === 'EMAIL_MISMATCH') {
        setPhase('mismatch');
      } else {
        setErrorMessage(err.message || 'Failed to accept invitation. Please try again.');
        setPhase('error');
      }
    }
  }, [token, currentUser, switchWorkspace, navigate]);

  useEffect(() => {
    if (!token) {
      setPhase('invalid');
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const result = await inviteApi.getDetails(token);
        if (cancelled) return;

        if (result.status === 'not_found') {
          setPhase('invalid');
          return;
        }
        if (result.status === 'accepted') {
          setPhase('already-accepted');
          return;
        }

        setInvite(result);
        sessionStorage.setItem(PENDING_TOKEN_KEY, token);
        sessionStorage.setItem(
          PENDING_CONTEXT_KEY,
          JSON.stringify({
            invitedEmail: result.invitedEmail || '',
            workspaceName: result.workspaceName || '',
            inviterName: result.inviterName || '',
          })
        );

        if (currentUser) {
          // Let the backend be the source of truth on the email match — this
          // exercises the real server-side guard, not just a client-side gate.
          setPhase('accepting');
        } else {
          setPhase('context');
        }
      } catch (err) {
        if (!cancelled) {
          setErrorMessage('Failed to load invitation. Please try again.');
          setPhase('error');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, currentUser]);

  useEffect(() => {
    if (phase === 'accepting' && invite && currentUser) {
      handleAccept();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, invite, currentUser]);

  async function handleSignOut() {
    sessionStorage.removeItem(PENDING_TOKEN_KEY);
    await logout();
    // Keep the invite context so the user lands back on the context card
    // for the correct address after signing out.
    setPhase('context');
  }

  return (
    <div className="auth-page">
      <div className="auth-wrap">
        <a href="https://gocta.ai" className="auth-wordmark">gocta<span className="accent">.ai</span></a>
        <div className="auth-card">
          {phase === 'loading' && (
            <>
              <h1 className="auth-title">Loading invitation...</h1>
              <p className="auth-subtitle">One moment while we look up your invite.</p>
            </>
          )}

          {phase === 'invalid' && (
            <>
              <h1 className="auth-title">Invitation not found</h1>
              <p className="auth-subtitle">
                This invitation link is invalid or has expired. Ask whoever invited you to send a new one.
              </p>
              <Link to="/login" className="auth-btn-primary" style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
                Go to login
              </Link>
            </>
          )}

          {phase === 'already-accepted' && (
            <>
              <h1 className="auth-title">Invitation already used</h1>
              <p className="auth-subtitle">
                This invitation has already been accepted. If this was you, just log in.
              </p>
              <Link to="/login" className="auth-btn-primary" style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
                Go to login
              </Link>
            </>
          )}

          {phase === 'context' && invite && (
            <>
              <h1 className="auth-title">You have been invited</h1>
              <p className="auth-subtitle">
                {invite.inviterName ? `${invite.inviterName} has invited you` : "You've been invited"} to join{' '}
                <strong>{invite.workspaceName || 'their workspace'}</strong> on gocta.ai.
              </p>

              <div className="auth-field">
                <label className="auth-label">Invited email</label>
                <input
                  type="email"
                  value={invite.invitedEmail || ''}
                  readOnly
                  className="auth-input"
                  style={{ background: 'var(--tint)', cursor: 'not-allowed' }}
                />
              </div>

              {invite.accountExists ? (
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="auth-btn-primary"
                >
                  Log in to accept
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate('/signup?invite=true')}
                  className="auth-btn-primary"
                >
                  Create account to accept
                </button>
              )}

              <div className="auth-footer">
                {invite.accountExists ? (
                  <Link to="/signup?invite=true" className="auth-link">
                    Need to create an account instead? Sign up
                  </Link>
                ) : (
                  <Link to="/login" className="auth-link">
                    Already have an account? Log in
                  </Link>
                )}
              </div>
            </>
          )}

          {phase === 'accepting' && (
            <>
              <h1 className="auth-title">Accepting invitation...</h1>
              <p className="auth-subtitle">Please wait while we add you to the workspace.</p>
            </>
          )}

          {phase === 'mismatch' && invite && (
            <>
              <h1 className="auth-title">Wrong account</h1>
              <p className="auth-subtitle">
                This invitation was sent to <strong>{invite.invitedEmail}</strong>, but you are logged in as{' '}
                <strong>{currentUser?.email}</strong>. Log out and continue with the invited address to accept.
              </p>
              <button type="button" onClick={handleSignOut} className="auth-btn-primary">
                Log out and continue
              </button>
            </>
          )}

          {phase === 'success' && (
            <>
              <h1 className="auth-title">Invitation accepted</h1>
              <p className="auth-subtitle">
                You've joined {invite?.workspaceName || 'the workspace'}. Redirecting to your dashboard...
              </p>
            </>
          )}

          {phase === 'error' && (
            <>
              <h1 className="auth-title">Something went wrong</h1>
              <div className="auth-error">{errorMessage}</div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <Link to="/login" className="auth-btn-primary" style={{ flex: 1, textAlign: 'center', textDecoration: 'none' }}>
                  Go to login
                </Link>
                <Link to="/dashboard" className="auth-link" style={{ flex: 1, textAlign: 'center', alignSelf: 'center' }}>
                  Go to dashboard
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
